import React from 'react';
import Dialog from '../../components/Dialog';
import { ArrowLeft, AlertCircleIcon } from 'lucide-react';
import StepProgressBar from '../../components/StepProgressBar';
import GeneralContractorForm from './GeneralContractorForm';
import { GeneralContractorFormData, FormErrors, Province } from './types';

interface GeneralContractorDialogsProps {
  // Dialog states
  showAddGeneralContractorDialog: boolean;
  showBlockDialog: boolean;
  showSaveConfirmDialog: boolean;
  showCloseConfirmDialog: boolean;
  isSubmitting: boolean;

  // Form data and steps
  formData: GeneralContractorFormData;
  errors: FormErrors;
  currentStep: number;
  editFormData: any;

  // References
  addGeneralContractorDialogContentRef: React.RefObject<HTMLDivElement>;

  // Options data
  provinceOptions: Province[];

  // Loading states
  isLoadingProvinces: boolean;

  // Deactivation eligibility
  canDeactivate?: boolean;
  deactivationReason?: string;

  // Status toggle visibility
  hideStatusToggle?: boolean;

  // Event handlers
  handleCloseAddGeneralContractorDialog: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleBack: () => void;
  handleNext: () => void;
  handleCancelSave: () => void;
  confirmSave: () => void;
  confirmBlock: () => void;
  setShowBlockDialog: (show: boolean) => void;
  setShowCloseConfirmDialog: (show: boolean) => void;
  pendingCloseAction: null | (() => void);
  setFormData: React.Dispatch<React.SetStateAction<GeneralContractorFormData>>;
}

const GeneralContractorDialogs: React.FC<GeneralContractorDialogsProps> = ({
  showAddGeneralContractorDialog,
  showBlockDialog,
  showSaveConfirmDialog,
  showCloseConfirmDialog,
  isSubmitting,
  formData,
  errors,
  currentStep,
  editFormData,
  addGeneralContractorDialogContentRef,
  provinceOptions,
  isLoadingProvinces,
  canDeactivate = true,
  deactivationReason = '',
  hideStatusToggle = false,
  handleCloseAddGeneralContractorDialog,
  handleInputChange,
  handleBack,
  handleNext,
  handleCancelSave,
  confirmSave,
  confirmBlock,
  setShowBlockDialog,
  setShowCloseConfirmDialog,
  pendingCloseAction,
  setFormData
}) => {
  const steps = ['Basic Information', 'Address Information'];

  return (
    <>
      {/* Add General Contractor Dialog */}
      <Dialog
        id="add-general-contractor-dialog"
        title={editFormData ? "Edit General Contractor" : "Add General Contractor"}
        isOpen={showAddGeneralContractorDialog}
        onClose={handleCloseAddGeneralContractorDialog}
        width="w-[95%] sm:w-[600px] md:w-[700px] lg:w-[500px]"
        padding="px-3 py-4 sm:px-4 sm:py-5 md:px-10 md:py-8 lg:px-6 lg:py-6"
      >
        {/* Add a max-height wrapper with scrolling */}
        <div ref={addGeneralContractorDialogContentRef} className="max-h-[70vh] overflow-y-auto ">
          {/* Sticky Header Section */}
          <div className="sticky top-0 bg-white z-10">
            {/* Progress Steps */}
            <StepProgressBar currentStep={currentStep} steps={steps} />
          </div>

          {/* Form Content */}
          <div className="mt-6">
            <GeneralContractorForm
              formData={formData}
              errors={errors}
              currentStep={currentStep}
              handleInputChange={handleInputChange}
              provinceOptions={provinceOptions}
              isLoadingProvinces={isLoadingProvinces}
              setFormData={setFormData}
              isEditMode={!!editFormData}
              canDeactivate={canDeactivate}
              deactivationReason={deactivationReason}
              hideStatusToggle={hideStatusToggle}
            />
          </div>

          {/* Navigation Buttons - Keep these fixed at the bottom */}
          <div className="sticky bottom-0 bg-white pt-8 mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                id="btn-previous"
                onClick={handleBack}
                className="btn-secondary z-9 flex items-center gap-2"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>
            )}
            <div className="flex-1" />
            <button
              id="btn-next"
              onClick={handleNext}
              className={`btn-primary z-9 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : currentStep === 2 ? (editFormData ? 'Save Changes' : 'Add General Contractor') : 'Next'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Block Confirmation Dialog */}
      <Dialog
        id="block-general-contractor-dialog"
        isOpen={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2">Are you sure?</h3>
          <p className="text-text-secondary mb-8">General contractor will be Inactive</p>

          <div className="flex justify-center gap-4">
            <button
              id="btn-block-no"
              onClick={() => setShowBlockDialog(false)}
              className="btn-secondary px-8 py-2"
            >
              No
            </button>
            <button
              id="btn-block-yes"
              onClick={confirmBlock}
              className="btn-primary px-8 py-2"
            >
              Yes
            </button>
          </div>
        </div>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog
        id="save-contractor-dialog"
        isOpen={showSaveConfirmDialog}
        onClose={handleCancelSave}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white flex items-center justify-center mx-auto mb-4">
              <AlertCircleIcon className="w-16 h-16 text-[#FF2C23]" />
            </div>
          </div>
          <h3 className="text-heading font-heading mb-2">Confirmation</h3>
          <p className="text-text-secondary mb-8">
            {editFormData
              ? `Are you sure you want to save changes to "${formData.name}"?`
              : `Are you sure to add the new general contractor "${formData.name}"?`}
          </p>

          <div className="flex justify-center gap-4">
            <button
              id="btn-cancel-save-contractor"
              onClick={handleCancelSave}
              className="btn-secondary px-8 py-2"
            >
              No
            </button>
            <button
              id="btn-save-contractor"
              onClick={() => confirmSave()}
              className="btn-primary px-8 py-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Yes'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Close Confirmation Dialog */}
      <Dialog
        id="close-contractor-dialog"
        isOpen={showCloseConfirmDialog}
        onClose={() => setShowCloseConfirmDialog(false)}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white flex items-center justify-center mx-auto mb-4">
              <AlertCircleIcon className="w-16 h-16 text-[#FF2C23]" />
            </div>
          </div>
          <h3 className="text-heading font-heading mb-2">Confirmation</h3>
          <p className="text-text-secondary mb-8">
            Are you sure you want to close? Unsaved changes will be lost.
          </p>
          <div className="flex justify-center gap-4">
            <button
              id="btn-close-no"
              onClick={() => setShowCloseConfirmDialog(false)}
              className="btn-secondary px-8 py-2"
            >
              No
            </button>
            <button
              id="btn-close-yes"
              onClick={() => {
                if (pendingCloseAction) pendingCloseAction();
              }}
              className="btn-primary px-8 py-2"
            >
              Yes
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default GeneralContractorDialogs;